import { aiProxy, AIProxyError, ProxyMessage, AIProposal } from './aiProxy';
import { mockAIService } from './mockAIService';

// The assistant's call layer. Routes the chat through the app-owned `ai-proxy`
// (no user API key); on rate-limit (429) or an unconfigured/unavailable server
// (503) it falls back to the offline mock so the assistant still responds.
//
// Phase 1 (AI_ASSISTANT_ARCHITECTURE §4a): the system prompt no longer ships
// in the bundle — the client sends `{ mode: 'assistant.chat', message,
// conversation, context, params }` and ai-proxy assembles the prompt from its
// server-side module (supabase/functions/ai-proxy/prompts/assistant.ts).

export interface AssistantReply {
  content: string;
  /** Daily usage from the proxy, when known. */
  usage?: { used: number; limit: number };
  /** True when the reply came from the offline mock (proxy unavailable / capped). */
  mock: boolean;
  /** True when the fallback was triggered specifically by hitting the daily cap. */
  rateLimited?: boolean;
  /** Write-tool proposals the user must confirm (Phase 2 tool-use). */
  proposals?: AIProposal[];
}

interface AssistantChatOptions {
  /** Prior turns + the new user message, oldest-first. */
  messages: ProxyMessage[];
  /** Pre-formatted user-context block (see utils/aiContext). */
  context?: string;
  /**
   * The read tools the user permits (`constants/aiVisibility.ts`).
   * ⚠️ `undefined` forwards nothing and the proxy applies no restriction — which
   * is what any caller that predates the visibility switches wants.
   */
  allowedTools?: string[];
}

export async function assistantChat(opts: AssistantChatOptions): Promise<AssistantReply> {
  // The mock is keyword-driven off the user turns — it never needed the real
  // system prompt, which now lives server-side only.
  const mockReply = async (): Promise<string> => (await mockAIService.chat(opts.messages)).content;

  // If we already know the proxy is unconfigured, don't make a doomed round-trip.
  if (aiProxy.isKnownUnavailable()) {
    return { content: await mockReply(), mock: true };
  }

  // Split prior turns from the new user message for the mode contract.
  const conversation = opts.messages.slice(0, -1);
  const last = opts.messages[opts.messages.length - 1];

  try {
    const res = await aiProxy.chat({ action: 'chat_message',
      mode: 'assistant.chat',
      conversation,
      message: last?.content ?? '',
      context: opts.context,
      // The user's LOCAL date, so "future" dates (quest byWhen, deadlines)
      // are computed against their day, not the server's UTC day.
      params: { today: new Date().toISOString().slice(0, 10) },
      allowedTools: opts.allowedTools,
      maxTokens: 2000,
      temperature: 0.7,
    });
    return { content: res.content, usage: res.usage, mock: false, proposals: res.proposals };
  } catch (error) {
    if (error instanceof AIProxyError && (error.rateLimited || error.status === 503)) {
      return {
        content: await mockReply(),
        mock: true,
        rateLimited: error.rateLimited,
        usage: error.usage,
      };
    }
    throw error;
  }
}
